import { useEffect, useMemo, useState } from "react";
import ReactModal from "react-modal";
import AddTagBasedSLAModal from "./AddTagBasedSLAModal";
import { ReactMultiEmail } from "react-multi-email";
import {
  useGetTicketTagSLAEscalationLevelsQuery,
  useUpdateTicketTagsMutation,
  useUpdateEscallationLevelMutation,
} from "../../../../Services/settingsAPIs/CommonAPI";
import { toast } from "react-toastify";
import DeleteLevelModal from "./tagBasedSLA/DeleteLevelModal";

const customAddlevelModalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    margin: "auto",
    Overflow: "hidden",
    OverflowY: "auto",
  },
};

const customModalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "450px",
    maxWidth: "850px",
    margin: "auto",
    Overflow: "hidden",
    OverflowY: "auto",
  },
};

interface Tag {
  id: number;
  tag_name: string;
  duration_in_ms: any;
  tag_id: any;
  tag_sla_mail: string[];
  team_id: number;
  level: { [key: string]: { email: any[]; duration_in_ms: any } };
  showExpanded?: any;
}

interface TimeValues {
  [key: string]: { hours: number; minutes: number };
}

const hasAtLeastOneEmail = (emails: unknown): boolean =>
  Array.isArray(emails) &&
  emails.some((e) => String(e ?? "").trim().length > 0);

/** Allow both unset; if either is set, both must be set (same as priority-based SLA). */
const validateEscalationDurationAndEmails = (
  durationInMs: number,
  emails: unknown
): { ok: true } | { ok: false; message: string } => {
  const emailsOk = hasAtLeastOneEmail(emails);
  const durationOk = durationInMs > 0;
  if (durationOk && emailsOk) return { ok: true };
  if (!durationOk && !emailsOk) return { ok: true };
  if (durationOk && !emailsOk) {
    return {
      ok: false,
      message:
        "SL duration is set but no notification email. Add at least one email.",
    };
  }
  return {
    ok: false,
    message:
      "Notification email is set but SL timer is not. Set hours or minutes.",
  };
};

const TagBasedSLA = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [theTags, setTheTags] = useState<any>([]);
  const [emailLists, setEmailLists] = useState({});
  const [levelEmailList, setLevelEmailList] = useState<any>({});
  const [timeValues, setTimeValues] = useState({});
  const [selectedLevelName, setSelectedLevelName] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentEscalationLevel, setCurrentEscalationLevel] = useState(1);
  const [updatedTimeValues, setUpdatedTimeValues] = useState({});
  const [updatedLevelTimeValues, setUpdatedLevelTimeValues] = useState({});
  const [selectedTag, setSelectedTag] = useState<any>(null);
  const [id, setId] = useState<any>(null);
  const [triggerTicketTag] = useUpdateTicketTagsMutation();
  const [triggerEscallationLevel] = useUpdateEscallationLevelMutation();

  const { data: ticketTagSLAEscalationLevels, refetch } =
    useGetTicketTagSLAEscalationLevelsQuery({});

  const openModal = (tagIndex: number) => {
    const selected = theTags[tagIndex];
    setSelectedTag(selected);

    // Check total existing levels (including Escalation Level 1)
    const existingLevelsCount = selected?.level
      ? Object.keys(selected.level).length
      : 1;

    // Maximum 5 escalation levels allowed
    if (existingLevelsCount >= 5) {
      toast.error("Limit exceeded! Maximum 5 escalation levels allowed.");
      return;
    }

    let availableLevels: any = new Set<number>();
    let minLevel = 2;

    if (selected?.level && Object.keys(selected?.level)?.length > 0) {
      const levels = Object.keys(selected.level)
        .map((level) => parseInt(level.match(/\d+/)?.[0] || "1", 10))
        .filter(Number.isFinite);

      const maxLevel = levels.length > 0 ? Math.max(...levels) + 1 : minLevel;
      for (let i = minLevel; i <= maxLevel; i++) {
        availableLevels.add(i);
      }
      levels.forEach((lvl) => availableLevels.delete(lvl));
    } else {
      availableLevels.add(minLevel);
    }
    setCurrentEscalationLevel(Math.min(...availableLevels));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };
  const openDeleteModal = (tagIndex: number, levelName: string) => {
    const selected = theTags[tagIndex];
    setSelectedTag(selected);
    setSelectedLevelName(levelName);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  useEffect(() => {

    console.log(ticketTagSLAEscalationLevels, "ticketTagSLAEscalationLevels")
    if (Array.isArray(ticketTagSLAEscalationLevels)) {
      const formattedTags = ticketTagSLAEscalationLevels.map((tag, index) => ({
        ...tag,
        showExpanded: theTags[index]?.showExpanded || false,
      }));

      setTheTags(formattedTags);

      const initialTimeValues: TimeValues = {};
      formattedTags.forEach((tag, tagIndex) => {
        if (typeof tag.duration_in_ms === "number" && tag.duration_in_ms > 0) {
          initialTimeValues[tagIndex] = {
            hours: Math.floor(tag.duration_in_ms / 3600000),
            minutes: Math.floor((tag.duration_in_ms % 3600000) / 60000),
          };
        }
        Object.entries(tag.level || {}).forEach(
          ([levelName, levelData]: [string, any]) => {
            const duration =
              typeof levelData.duration_in_ms === "number"
                ? levelData.duration_in_ms
                : 0;
            initialTimeValues[`${tagIndex}-${levelName}`] = {
              hours: Math.floor(duration / 3600000),
              minutes: Math.floor((duration % 3600000) / 60000),
            };
          }
        );
      });
      setTimeValues(initialTimeValues);
    }
  }, [ticketTagSLAEscalationLevels]);

  const toggleExpansion = (index: number) => {
    setTheTags((prev) =>
      prev.map((tag, i) =>
        i === index ? { ...tag, showExpanded: !tag.showExpanded } : tag
      )
    );
  };

  const handleTimeChangeLevel1 = (tagIndex, unit, action) => {
    setTimeValues((prev) => {
      const currentValue = prev[tagIndex] || { hours: 0, minutes: 0 };

      const updatedValue = {
        ...currentValue,
        [unit]:
          action === "increment"
            ? currentValue[unit] + 1
            : Math.max(currentValue[unit] - 1, 0),
      };

      setUpdatedTimeValues((prevUpdates) => ({
        ...prevUpdates,
        [tagIndex]: { ...updatedValue }, // Ensuring immutability
      }));

      return { ...prev, [tagIndex]: { ...updatedValue } };
    });
  };

  const handleTimeChangeExpanded = (tagIndex, levelName, unit, action) => {
    const key = `${tagIndex}-${levelName}`;

    setTimeValues((prev) => {
      const currentValue = prev[key] || { hours: 0, minutes: 0 };
      const updatedValue = {
        ...currentValue,
        [unit]:
          action === "increment"
            ? currentValue[unit] + 1
            : Math.max(currentValue[unit] - 1, 0),
      };

      setUpdatedLevelTimeValues((prevUpdates) => ({
        ...prevUpdates,
        [key]: { ...updatedValue }, // Ensuring immutability
      }));

      return { ...prev, [key]: { ...updatedValue } };
    });
  };

  const handleTimeChange = (tagIndex, levelName, unit, action) => {
    if (levelName) {
      handleTimeChangeExpanded(tagIndex, levelName, unit, action);
    } else {
      handleTimeChangeLevel1(tagIndex, unit, action);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    let anyUpdated = false;
    try {
      for (let tagIndex = 0; tagIndex < theTags.length; tagIndex++) {
        const tag: any = theTags[tagIndex];
        const updatedTime =
          updatedLevelTimeValues[`${tagIndex}-Escalation Level 1`];
        const updatedEmails = emailLists[tagIndex];
        const origHours = Math.floor(
          (tag.level?.["Escalation Level 1"]?.duration_in_ms || 0) / 3600000
        );
        const origMinutes = Math.floor(
          ((tag.level?.["Escalation Level 1"]?.duration_in_ms || 0) % 3600000) /
          60000
        );
        const origEmails = tag.level?.["Escalation Level 1"]?.sla_mail || [];

        let changed = false;
        let hours = origHours;
        let minutes = origMinutes;
        let emails = origEmails;
        if (updatedTime) {
          hours = updatedTime.hours;
          minutes = updatedTime.minutes;
          if (hours !== origHours || minutes !== origMinutes) changed = true;
        }
        if (
          updatedEmails !== undefined &&
          JSON.stringify(updatedEmails) !== JSON.stringify(origEmails)
        ) {
          emails = [...updatedEmails]; // force copy to avoid mutating original array
          changed = true;
        }
        if (changed) {
          const duration_in_ms = hours * 3600000 + minutes * 60000;
          const rowValid = validateEscalationDurationAndEmails(
            duration_in_ms,
            emails
          );
          if (rowValid.ok === false) {
            toast.error(rowValid.message);
            setIsLoading(false);
            return;
          }
          anyUpdated = true;
          const payload = {
            sla_id: tag.sla_id,
            level: "Escalation Level 1",
            value: {
              sla_mail: emails,
              duration_in_ms,
            },
          };
          await triggerTicketTag({
            body: payload,
            id: tag.tag_id,
          });
        }
      }
      if (anyUpdated) {
        toast.success("Escalation level updated successfully");
        refetch();
      } else {
        toast.warn("No changes detected in Escalation Level 1");
      }
      closeModal();
      setIsLoading(false);
    } catch (error) {
      console.error("Error saving escalation:", error);
      toast.error("Failed to save escalation. Please try again.");
      setIsLoading(false);
    }
  };

  const handleEscalationLevelUpdate = async (tagIndex, levelName) => {
    const levelKey = levelName;
    const key = `${tagIndex}-${levelName}`;

    const previousTimeValues = timeValues[key] || { hours: 0, minutes: 0 };
    const updatedTime = updatedLevelTimeValues[key] || {};

    const finalHours =
      updatedTime.hours !== undefined
        ? updatedTime.hours
        : previousTimeValues.hours;
    const finalMinutes =
      updatedTime.minutes !== undefined
        ? updatedTime.minutes
        : previousTimeValues.minutes;
    const durationInMs = finalHours * 3600000 + finalMinutes * 60000;

    const originalData = theTags[tagIndex]?.level?.[levelName] || {};
    const originalEmails = originalData.sla_mail || [];
    const originalDuration = originalData.duration_in_ms || 0;
    const originalHours = Math.floor(originalDuration / 3600000);
    const originalMinutes = Math.floor((originalDuration % 3600000) / 60000);

    const finalEmails =
      levelEmailList[key] !== undefined
        ? [...(levelEmailList[key] || [])]
        : [...originalEmails];

    let hasTimeChanges = false;
    let hasEmailChanges = false;
    if (finalHours !== originalHours || finalMinutes !== originalMinutes) {
      hasTimeChanges = true;
    }
    if (
      JSON.stringify(finalEmails.sort()) !==
      JSON.stringify([...originalEmails].sort())
    ) {
      hasEmailChanges = true;
    }

    const hasChanges = hasTimeChanges || hasEmailChanges;

    if (!hasChanges) {
      toast.warn(`No changes detected for ${levelName}`);
      return;
    }

    const rowValid = validateEscalationDurationAndEmails(
      durationInMs,
      finalEmails
    );
    if (rowValid.ok === false) {
      toast.error(rowValid.message);
      return;
    }

    const payload = {
      sla_id: theTags[tagIndex]?.sla_id,
      level: levelKey,
      value: {
        sla_mail: finalEmails,
        duration_in_ms: durationInMs,
      },
    };

    try {
      setIsLoading(true);
      await triggerEscallationLevel({
        body: payload,
        id: theTags[tagIndex]?.tag_id,
      });
      toast.success("Escalation level updated successfully");

      setUpdatedLevelTimeValues((prev: any) => {
        const { [key]: removed, ...rest } = prev;
        return rest;
      });
      setLevelEmailList((prev) => {
        const { [key]: removed, ...rest } = prev;
        return rest;
      });

      refetch();
      setIsLoading(false);
    } catch (error) {
      console.error("Error saving escalation:", error);
      toast.error("Failed to update escalation. Please try again.");
      setIsLoading(false);
    }
  };

  const hasLevelChanges = (tagIndex, levelName) => {
    const key = `${tagIndex}-${levelName}`;
    const updatedTime = updatedLevelTimeValues[key];

    const originalData = theTags[tagIndex]?.level?.[levelName] || {};
    const originalEmails = originalData.sla_mail || [];
    const originalDuration = originalData.duration_in_ms || 0;
    const originalHours = Math.floor(originalDuration / 3600000);
    const originalMinutes = Math.floor((originalDuration % 3600000) / 60000);

    let hasTimeChanges = false;
    if (updatedTime) {
      const currentHours =
        updatedTime?.hours !== undefined ? updatedTime?.hours : originalHours;
      const currentMinutes =
        updatedTime?.minutes !== undefined
          ? updatedTime?.minutes
          : originalMinutes;
      hasTimeChanges =
        currentHours !== originalHours || currentMinutes !== originalMinutes;
    }

    const draftEmails = levelEmailList[key];
    const hasEmailChanges =
      draftEmails !== undefined &&
      JSON.stringify([...draftEmails].sort()) !==
      JSON.stringify([...originalEmails].sort());

    return hasTimeChanges || hasEmailChanges;
  };

  const getEscalationFinalState = (tagIndex: number, levelName: string) => {
    const key = `${tagIndex}-${levelName}`;
    const previousTimeValues = timeValues[key] || { hours: 0, minutes: 0 };
    const updatedTime = updatedLevelTimeValues[key] || {};
    const finalHours =
      updatedTime.hours !== undefined
        ? updatedTime.hours
        : previousTimeValues.hours;
    const finalMinutes =
      updatedTime.minutes !== undefined
        ? updatedTime.minutes
        : previousTimeValues.minutes;
    const durationInMs = finalHours * 3600000 + finalMinutes * 60000;
    const originalData = theTags[tagIndex]?.level?.[levelName] || {};
    const originalEmails = originalData.sla_mail || [];

    let finalEmails: unknown[];
    if (levelName === "Escalation Level 1") {
      const draft = emailLists[tagIndex];
      finalEmails =
        draft !== undefined ? [...draft] : [...originalEmails];
    } else {
      const draft = levelEmailList[key];
      finalEmails =
        draft !== undefined ? [...(draft || [])] : [...originalEmails];
    }

    return { durationInMs, finalEmails };
  };

  const isEscalationRowValidForSave = (tagIndex: number, levelName: string) => {
    const { durationInMs, finalEmails } = getEscalationFinalState(
      tagIndex,
      levelName
    );
    return validateEscalationDurationAndEmails(durationInMs, finalEmails).ok;
  };

  const hasEscalationLevel1DirtyChanges = useMemo(() => {
    if (!Array.isArray(theTags) || theTags.length === 0) return false;
    for (let tagIndex = 0; tagIndex < theTags.length; tagIndex++) {
      const tag = theTags[tagIndex];
      const updatedTime =
        updatedLevelTimeValues[`${tagIndex}-Escalation Level 1`];
      const updatedEmails = emailLists[tagIndex];
      const level1 = tag.level?.["Escalation Level 1"];
      const origMs = level1?.duration_in_ms || 0;
      const origHours = Math.floor(origMs / 3600000);
      const origMinutes = Math.floor((origMs % 3600000) / 60000);
      const origEmails = level1?.sla_mail || [];

      const timeDirty =
        !!updatedTime &&
        (updatedTime.hours !== origHours || updatedTime.minutes !== origMinutes);
      const emailDirty =
        updatedEmails !== undefined &&
        JSON.stringify(updatedEmails) !== JSON.stringify(origEmails);

      if (timeDirty || emailDirty) return true;
    }
    return false;
  }, [theTags, updatedLevelTimeValues, emailLists]);

  return (
    <div style={{ marginLeft: "0%" }}>
      <div className="row">
        <div className="col">
          <div
            className="card my-1 min-h-40px justify-content-center"
            style={{ backgroundColor: "rgba(228, 228, 228, 0.498)" }}
          >
            <div className="row align-items-center">
              <div className="col-2 text-start ps-10">
                <strong>Tag</strong>
              </div>
              <div className="col-2 text-center">
                <strong>Escalation Level</strong>
              </div>
              <div className="col-2 text-center">
                <strong>Hours</strong>
              </div>
              <div className="col-2 text-center">
                <strong>Minutes</strong>
              </div>
              <div className="col-2 text-center">
                <strong>Mail ID</strong>
              </div>
              <div className="col-2 text-center">
                <strong>Actions</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={{ maxHeight: "60vh", overflowY: "auto", overflowX: "hidden" }}>
        {theTags.map((tagInfo: any, tagIndex: any) => (
          <div key={tagIndex}>
            <div className="row">
              <div className="col">
                <div className="input-group-text mt-2 bg-white min-w-600px">
                  <div className="row w-100 align-items-center text-center">
                    <div className="col-2 text-start ps-10">{tagInfo.tag}</div>
                    <div className="col-2">Escalation Level 1</div>
                    <div className="col-2 d-flex align-items-center justify-content-center">
                      {timeValues[`${tagIndex}-Escalation Level 1`]?.hours || 0}
                      <div className="d-flex flex-column ms-2">
                        <i
                          className="bi bi-caret-up-fill cursor-pointer"
                          onClick={() => {
                            handleTimeChange(
                              tagIndex,
                              "Escalation Level 1",
                              "hours",
                              "increment"
                            );
                            setId(tagInfo);
                          }}
                        ></i>
                        <i
                          className="bi bi-caret-down-fill cursor-pointer"
                          onClick={() => {
                            handleTimeChange(
                              tagIndex,
                              "Escalation Level 1",
                              "hours",
                              "decrement"
                            );
                            setId(tagInfo);
                          }}
                        ></i>
                      </div>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-center">
                      {timeValues[`${tagIndex}-Escalation Level 1`]?.minutes || 0}
                      <div className="d-flex flex-column ms-2">
                        <i
                          className="bi bi-caret-up-fill cursor-pointer"
                          onClick={() => {
                            handleTimeChange(
                              tagIndex,
                              "Escalation Level 1",
                              "minutes",
                              "increment"
                            );
                            setId(tagInfo);
                          }}
                        ></i>
                        <i
                          className="bi bi-caret-down-fill cursor-pointer"
                          onClick={() => {
                            handleTimeChange(
                              tagIndex,
                              "Escalation Level 1",
                              "minutes",
                              "decrement"
                            );
                            setId(tagInfo);
                          }}
                        ></i>
                      </div>
                    </div>

                    <div className="col-3">
                      <ReactMultiEmail
                        placeholder="Enter Email"
                        emails={
                          emailLists[tagIndex] ||
                          tagInfo.level?.["Escalation Level 1"]?.sla_mail ||
                          []
                        }
                        onChange={(updatedEmails) => {
                          setEmailLists((prev) => ({
                            ...prev,
                            [tagIndex]: [...updatedEmails],
                          }));
                          setId(tagInfo);
                        }}
                        getLabel={(email, index, removeEmail) => (
                          <div data-tag key={index}>
                            {email}
                            <span
                              data-tag-handle
                              onClick={() => removeEmail(index)}
                            >
                              ×
                            </span>
                          </div>
                        )}
                      />
                    </div>
                    <div className="col-1 d-flex justify-content-center">
                      <button
                        className="btn btn-link py-0"
                        onClick={() => openModal(tagIndex)}
                      >
                        <i className="text-dark bi bi-plus-lg"></i>
                      </button>
                      <button
                        className="btn btn-link py-0"
                        onClick={() => toggleExpansion(tagIndex)}
                      >
                        <i
                          className={`ms-2 text-dark ${tagInfo.showExpanded
                              ? "bi bi-chevron-up"
                              : "bi bi-chevron-down"
                            }`}
                        ></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {tagInfo.showExpanded &&
              Object.entries(tagInfo.level || {})
                .filter(([levelName]) => levelName !== "Escalation Level 1")
                .map(([levelName, levelData]: [string, any], levelIndex) => (
                  <div className="row" key={levelName}>
                    <div className="col">
                      <div className="input-group-text mt-2 bg-light min-w-600px">
                        <div className="row w-100 align-items-center">
                          <div className="col-2 text-start ps-10"></div>
                          <div className="col-2 text-center">{levelName}</div>
                          <div className="col-2 d-flex align-items-center justify-content-center">
                            {timeValues[`${tagIndex}-${levelName}`]?.hours || 0}
                            <div className="d-flex flex-column ms-2">
                              <i
                                className="bi bi-caret-up-fill cursor-pointer"
                                onClick={() => {
                                  handleTimeChange(
                                    tagIndex,
                                    levelName,
                                    "hours",
                                    "increment"
                                  );
                                  setId(tagInfo);
                                }}
                              ></i>
                              <i
                                className="bi bi-caret-down-fill cursor-pointer"
                                onClick={() => {
                                  handleTimeChange(
                                    tagIndex,
                                    levelName,
                                    "hours",
                                    "decrement"
                                  );
                                  setId(tagInfo);
                                }}
                              ></i>
                            </div>
                          </div>
                          <div className="col-2 d-flex align-items-center justify-content-center">
                            {timeValues[`${tagIndex}-${levelName}`]?.minutes || 0}
                            <div className="d-flex flex-column ms-2">
                              <i
                                className="bi bi-caret-up-fill cursor-pointer"
                                onClick={() => {
                                  handleTimeChange(
                                    tagIndex,
                                    levelName,
                                    "minutes",
                                    "increment"
                                  );
                                  setId(tagInfo);
                                }}
                              ></i>
                              <i
                                className="bi bi-caret-down-fill cursor-pointer"
                                onClick={() => {
                                  handleTimeChange(
                                    tagIndex,
                                    levelName,
                                    "minutes",
                                    "decrement"
                                  );
                                  setId(tagInfo);
                                }}
                              ></i>
                            </div>
                          </div>
                          <div className="col-3 text-center">
                            <ReactMultiEmail
                              placeholder="Enter Email"
                              emails={
                                levelEmailList[`${tagIndex}-${levelName}`] ||
                                levelData.sla_mail ||
                                []
                              }
                              onChange={(updatedEmails) => {
                                const rowKey = `${tagIndex}-${levelName}`;
                                setLevelEmailList((prev) => ({
                                  ...prev,
                                  [rowKey]: [...updatedEmails],
                                }));
                                setId(tagInfo);
                              }}
                              getLabel={(email, index, removeEmail) => (
                                <div data-tag key={index}>
                                  {email}
                                  <span
                                    data-tag-handle
                                    onClick={() => removeEmail(index)}
                                  >
                                    ×
                                  </span>
                                </div>
                              )}
                            />
                          </div>
                          <div className="col-1 text-center">
                            <div className="d-flex justify-content-center align-items-center gap-2">
                              <button
                                type="button"
                                className="btn btn-link text-danger p-0"
                                title="Delete Escalation Level"
                                onClick={() =>
                                  openDeleteModal(tagIndex, levelName)
                                }
                              >
                                <i className="bi bi-trash text-danger fs-5"></i>
                              </button>
                              <button
                                type="button"
                                className={`btn btn-link p-0`}
                                title="Update Escalation Level"
                                onClick={() =>
                                  handleEscalationLevelUpdate(tagIndex, levelName)
                                }
                                disabled={
                                  !hasLevelChanges(tagIndex, levelName) || isLoading
                                }
                              >
                                <i
                                  className={`bi bi-check-lg fs-3 ${hasLevelChanges(tagIndex, levelName) &&
                                      isEscalationRowValidForSave(
                                        tagIndex,
                                        levelName
                                      )
                                      ? "text-success"
                                      : "text-muted"
                                    }`}
                                ></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
          </div>
        ))}
      </div>
      <div className="row mt-4">
        <div className="col d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-primary ms-4 min-w-100px"
            onClick={handleSave}
            disabled={isLoading || !hasEscalationLevel1DirtyChanges}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
      <ReactModal
        isOpen={isModalOpen}
        style={customAddlevelModalStyles}
        onRequestClose={closeModal}
        contentLabel="New Ticket"
        shouldCloseOnOverlayClick={false}
      >
        <AddTagBasedSLAModal
          closeModal={closeModal}
          currentEscalationLevel={currentEscalationLevel}
          refetch={refetch}
          selectedTag={selectedTag}
        />
      </ReactModal>

      <ReactModal
        isOpen={isDeleteModalOpen}
        style={customModalStyles}
        onRequestClose={closeDeleteModal}
        contentLabel="Delete level"
        shouldCloseOnOverlayClick={false}
      >
        <DeleteLevelModal
          closeDeleteModal={closeDeleteModal}
          levelName={selectedLevelName}
          tagId={selectedTag?.tag_id}
          refetch={refetch}
          selectedSlaId={selectedTag?.sla_id}
        />
      </ReactModal>
    </div>
  );
};

export default TagBasedSLA;
